<template>
  <component
      :is="player"
      v-bind="$attrs"
      :options="currentVideoOptions"
      :analytics-options="analyticsOptions"
      :analytics-allowed="analyticsAllowed"
      @interface="onInterfaceGet"
      @play="onPlayChange"
      @firstFrame="onFirstFrame"
      @ready="onReady"
      @complete="onComplete"
      @error="onError"
      @seek="onPlayChange"
  />
</template>

<script>
import VideoPlayer from '@/components/video/VideoPlayer.vue';
import TheoPlayerComponent from '@/components/video/TheoPlayerComponent.vue';
import {PLAYER_ACTIONS, STREAM_STATUS} from '@/const';
import VisaicV3Analytics from '@/components/base-components/Analytics/VisaicV3Analytics';
import {mapState} from 'vuex';
import MuxAnalytics from '@/mixins/MuxAnalytics';

export default {
  name: 'PlayerController',
  PLAYERS: {
    jw: VideoPlayer,
    theo: TheoPlayerComponent,
  },
  V3_INTERVAL_DURATION: 60 * 1000,
  mixins: [MuxAnalytics],
  props: {
    playerType: {
      type: String,
      validator(type) {
        const allowedTypes = ['jw', 'theo'];
        return allowedTypes.includes(type);
      },
      default: () => 'jw',
    },
    event: {
      type: Object,
      required: true,
    },
    analyticsOptions: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    sport: {
      type: String,
    },
    teams: {
      type: Object,
    },
    analyticsAllowed: {
      type: Boolean,
      default: () => true,
    },
    options: {
      type: Object,
      required: true,
      default: () => ({}),
    },
  },
  data() {
    return {
      v3VideoUUID: null,
      trackingInterval: null,
      startTime: null,
      totalAccumulatedWatchTime: 0,
      playing: false,
      onSeekTimeout: null,
      currentVideoOptions: {},
      customPreRolls: [...(this.options?.customPreRolls?.list || [])],
    };
  },
  mounted() {
    this.currentVideoOptions = this.nextPlayedFile;
  },
  computed: {
    ...mapState({
      V3AnalyticsProfile: (state) => {
        return state.channel?.settings?.['yare-analytics']?.['yare-analytics-key'];
      },
      defaultVideoPlayer: (state) => {
        return state.channel.settings?.content?.['default_video_player'] || 'jw';
      },
    }),
    nextPlayedFile() {
      if (this.customPreRolls.length) {
        return Object.assign({}, this.options, this.customPreRolls[0]);
      }
      return this.options;
    },
    isCustomPreRollPlay() {
      return this.customPreRolls?.[0] &&
        this.currentVideoOptions?.file === this.customPreRolls?.[0]?.file;
    },
    V3AnalyticsAllowed() {
      return this.analyticsAllowed && this.V3AnalyticsProfile;
    },
    muxAnalyticsAllowed() {
      return this.analyticsAllowed && this.getPlayerType === 'jw';
    },
    getPlayerType() {
      return this.event?.video_player || this.defaultVideoPlayer || this.playerType || 'jw';
    },
    player() {
      return this.$options.PLAYERS[this.getPlayerType];
    },
    mediaType() {
      return this.event.stream_status === STREAM_STATUS.vod ? 'VOD' : 'LIVE';
    },
    gender() {
      return this.teams.homeTeam.gender;
    },
    watchedTime() {
      const activeSessionTime = this.startTime ? new Date() - this.startTime : 0;
      return this.totalAccumulatedWatchTime + activeSessionTime;
    },
  },
  methods: {
    onInterfaceGet(playerInterface) {
      if (this.isCustomPreRollPlay) {
        this.$emit('interface', null);
        return;
      }
      this.$emit('interface', playerInterface);
      this.muxAnalyticsAllowed && playerInterface?.player &&
        this.setupMuxAnalytics(playerInterface?.player);
    },
    onPlayChange(playBool) {
      this.$emit(PLAYER_ACTIONS.play, playBool);
      if (playBool) {
        this.resumeTracking();
      } else {
        this.pauseTracking();
      }
    },
    resumeTracking() {
      if (this.playing) return;
      this.playing = true;

      if (!this.startTime) {
        this.startTime = new Date();
      }

      if (!this.trackingInterval) {
        this.trackingInterval = setInterval(() => {
          this.trackTime();
        }, this.$options.V3_INTERVAL_DURATION);
      }
    },
    pauseTracking() {
      if (!this.playing) return;
      this.playing = false;

      if (this.startTime) {
        this.totalAccumulatedWatchTime += new Date() - this.startTime;
      }
      if (this.totalAccumulatedWatchTime > 0) {
        this.trackTime(true);
      }

      this.startTime = null;
      clearInterval(this.trackingInterval);
      this.trackingInterval = null;
    },
    onSeek() {
      this.pauseTracking();
      this.onSeekTimeout = setTimeout(() => {
        if (this.playing) {
          this.onSeekTimeout = null;
          this.resumeTracking();
        }
      }, 1000);
    },
    onReady() {
      this.sendEvent(PLAYER_ACTIONS.ready);
    },
    onFirstFrame() {
      this.sendEvent(PLAYER_ACTIONS.first_frame);
      this.onPlayChange(true);
    },
    onComplete() {
      this.trackTime();
      this.sendEvent(PLAYER_ACTIONS.complete);
      if (this.isCustomPreRollPlay) {
        this.currentVideoOptions?.onComplete?.();
        this.customPreRolls = this.customPreRolls.slice(1);
        this.currentVideoOptions = this.nextPlayedFile;
      }
    },
    onError(error = '') {
      this.sendEvent(PLAYER_ACTIONS.error, error);
    },
    sendEvent(name, description) {
      if (!this.V3AnalyticsAllowed || this.isCustomPreRollPlay) return;

      this.v3VideoUUID = VisaicV3Analytics.videoEvent({
        uuid: this.v3VideoUUID,
        streamId: this.event.id,
        type: this.mediaType,
        event: name,
        extraData: {
          description: description,
          streamEvent: {
            description: this.event?.description,
            event_category_id: this.event?.event_category_id,
            home_team_id: this.event?.home_team_id,
            is_playoff: this.event?.is_playoff,
            name: this.event?.name,
            stream_name: this.event?.stream_name,
            stream_status: this.event?.stream_status,
            time_zone: this.event?.time_zone,
            type: this.event?.type,
            event: name,
          },
        },
      });
    },
    trackTime(forceSend) {
      if (!this.V3AnalyticsAllowed || this.isCustomPreRollPlay) return;

      const totalWatchedSeconds = Math.round(this.watchedTime / 1000);

      if (totalWatchedSeconds > 0 && (totalWatchedSeconds >= 30 || forceSend)) {
        VisaicV3Analytics.videoViewingTime({
          uuid: this.v3VideoUUID,
          streamId: this.event.id,
          type: this.mediaType,
          time: totalWatchedSeconds,
          category: this.sport,
          sport: this.sport,
          gender: this.gender,
        });

        this.totalAccumulatedWatchTime = 0;
        if (this.playing) {
          this.startTime = new Date();
        } else {
          this.startTime = null;
        }
      }
    },
  },
  beforeDestroy() {
    this.trackTime(true);
    clearTimeout(this.onSeekTimeout);
    clearInterval(this.trackingInterval);
  },
};
</script>
